import { AvatarDropdown, AvatarName, Footer, NoticeIcon, LanguageSwitch } from '@/components';
import ProjectSelector from '@/components/ProjectSelector';
import LayoutActions from '@/components/LayoutActions';
import LayoutLinks from '@/components/LayoutLinks';
import { getProjectProcesses } from '@/services/ant-design-pro/api';
import { ProLayout, SettingDrawer } from '@ant-design/pro-components';
import {
  BarChartOutlined,
  ProjectOutlined,
  FileTextOutlined,
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { Outlet, useLocation } from 'react-router';
import { useLocale } from '@/hooks/useLocale';
import { useProcessStore } from '@/store/process';
import { useAuthStore } from '@/store/auth';
import { useAccess } from '@/hooks/useAccess';
import { canManageProject } from '@/utils/permission';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import routes from '../router/routes';
import useAuth from '@/hooks/useAuth';
import ContentLoading from '@/components/ContentLoading';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

// 路由 meta.icon 配置的是图标名字符串，这里映射为实际的图标组件
const menuIconMap: Record<string, React.ComponentType> = {
  BarChartOutlined,
  ProjectOutlined,
  FileTextOutlined,
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  UserOutlined,
};

const renderMenuIcon = (iconName?: string) => {
  if (!iconName) return undefined;
  const IconComponent = menuIconMap[iconName];
  return IconComponent ? <IconComponent /> : undefined;
};

const BasicLayout: React.FC = () => {
  const { processes, setProcesses } = useProcessStore();
  const { currentUser, currentProject, settings } = useAuthStore();
  const { clearAuthData } = useAuth();
  const access = useAccess();

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { getLocale } = useLocale();

  useEffect(() => {
    if (currentProject?.id) {
      getProjectProcesses(Number(currentProject.id)).then((response) => {
        if (response.success) {
          setProcesses(response.data);
        }
      });
    }
  }, [currentProject?.id]);

  useEffect(() => {
    // 登录状态访问根路径时，自动跳转到对应的页面
    const user = JSON.parse(localStorage.getItem('current_user') || 'null');
    if (user && location.pathname === '/') {
      let path = '/overview';
      if (user.isAdmin) {
        path = '/system/overview';
      } else if (user.isAspiceSpecialist) {
        path = '/aspice/overview';
      }

      navigate(path, { replace: true });
    }

    // 多 tab 页监听 localStorage 变化，如果 current_user 被清空，则清除登录状态
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'current_user' && !event.newValue) {
        clearAuthData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const translateMenuName = (menuItem: { name?: string; locale?: string }) => {
    if (!menuItem.name) return menuItem.name;
    const key = typeof menuItem.locale === 'string' ? menuItem.locale : `menu.${menuItem.name}`;
    return t(key);
  };

  const convertRoutesToMenuData = (routeList: any[]): any[] => {
    return (
      routeList
        // 只有配置了 meta.name 的路由才是有效菜单项，过滤掉重定向、404 等无名路由；
        // 同时按 meta.access 过滤掉当前用户没有权限的菜单项
        .filter(
          (route) =>
            route.meta?.name &&
            !route.meta?.hideInMenu &&
            (!route.meta?.access || access[route.meta.access as keyof typeof access]),
        )
        .map((route) => {
          const menuItem: any = {
            path: route.path,
            name: route.meta?.name,
            icon: renderMenuIcon(route.meta?.icon),
            locale: route.meta?.locale,
            hideInMenu: route.meta?.hideInMenu,
          };

          if (route.children && route.children.length > 0) {
            menuItem.children = convertRoutesToMenuData(route.children);
          }

          return menuItem;
        })
    );
  };

  const menuDataRender = () => {
    // 只取当前布局（path: '/'）自身的子路由用于生成菜单，
    // 避免把 /user、/aspice 等同级布局路由也混入菜单数据。
    const basicLayoutRoute = routes.find((route) => route.path === '/');
    const menuData = convertRoutesToMenuData(basicLayoutRoute?.children ?? []);

    return menuData.map((item: any) => {
      if (item.path === '/project') {
        const isProjectOwner = canManageProject(currentUser, currentProject);
        return {
          ...item,
          name: translateMenuName(item),
          hideInMenu: !isProjectOwner,
          locale: false,
          children: item.children?.map((child: any) => ({
            ...child,
            name: translateMenuName(child),
            locale: false,
          })),
        };
      }
      if (item.path === '/docs') {
        return {
          ...item,
          name: translateMenuName(item),
          locale: false,
          children: processes
            .filter((process: any) => process.status === 'ENABLED')
            .map((process: any) => {
              const isProcessOwner = process.owner?.id === currentUser?.id;
              const processNameKey = `menu.process.${process.processCode}`;
              const localizedProcessName = t(processNameKey);
              return {
                path: `/docs/process/${process.processCode}`,
                name: isProcessOwner ? `⭐️ ${localizedProcessName}` : localizedProcessName,
                icon: null,
                locale: false,
              };
            }),
        };
      }
      return {
        ...item,
        name: translateMenuName(item),
        locale: false,
      };
    });
  };

  return (
    <ProLayout
      title={getLocale() === 'en-US' ? 'AsPiCe Development System' : 'AsPiCe 开发系统'}
      logo="/logo.png"
      layout="mix"
      siderWidth={256}
      fixSiderbar
      fixedHeader
      headerContentRender={() => {
        return !currentUser?.isAdmin ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '24px',
            }}
          >
            <ProjectSelector />
          </div>
        ) : null;
      }}
      actionsRender={() => [
        <LayoutActions key="actions" currentUser={currentUser} />,
        <LanguageSwitch key="language" />,
        <NoticeIcon key="notice" />,
      ]}
      avatarProps={{
        title: <AvatarName />,
        render: (_, avatarChildren) => {
          return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
        },
      }}
      waterMarkProps={{
        content: currentUser?.name,
      }}
      footerRender={() => <Footer />}
      links={LayoutLinks()}
      menuHeaderRender={undefined}
      childrenRender={(children: React.ReactNode) => (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={settings}
              onSettingChange={(newSettings) => {
                useAuthStore.getState().setSettings(newSettings);
              }}
            />
          )}
        </>
      )}
      {...settings}
      menuDataRender={menuDataRender}
      menuItemRender={(item, dom) => (
        <div onClick={() => navigate(item.path || '/overview')}>{dom}</div>
      )}
    >
      <Suspense fallback={<ContentLoading />}>
        <Outlet />
      </Suspense>
    </ProLayout>
  );
};

export default BasicLayout;
